import React from 'react';
import {
    AppBar,
    Box,
    Button,
    CircularProgress,
    Container,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    ListSubheader,
    Menu,
    MenuItem,
    Select,
    Toolbar,
    Tooltip,
    Typography,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutIcon from '@mui/icons-material/Logout';
import { Refresh } from '@mui/icons-material';
import logo from '../assets/images/entrada-facil.svg';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { getCaixaAbertoPedidosByUsuario } from '../services/adminCaixaMovimento';
import { savePrinterConfig } from '../utils/printerRouting';

interface AdminLayoutProps {
    children: React.ReactNode;
}

export default function AdminLayout({ children }: AdminLayoutProps) {
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [carregandoImpressoras, setCarregandoImpressoras] = React.useState(false);
    const [impressoras, setImpressoras] = React.useState<any[]>([]);
    const [impressoraSelecionada, setImpressoraSelecionada] = React.useState<string | "">("");
    const [temCaixaAberto, setTemCaixaAberto] = React.useState(false);

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => setAnchorEl(null);

    const navigateTo = (path: string) => {
        handleMenuClose();
        navigate(path);
    };

    const handleIngressos = () => {
        navigateTo('/admin/IngressosVendidos')
    }

    const handleInicio = () => {
        navigateTo('/admin')
    }

    const handleCheckin = () => {
        navigateTo('/admin/checkin')
    }

    const handleEmpresas = () => {
        navigateTo('/admin/empresas')
    }

    const handleTiposIngressos = () => {
        navigateTo('/admin/tipos-ingressos')
    }

    const handleIngressosCrud = () => {
        navigateTo('/admin/ingressos')
    }

    const handleTestesImpressora = () => {
        navigateTo('/admin/testes-impressora')
    }

    const handlePedidos = () => {
        navigateTo('/admin/financeiro/pedidos')
    }

    const handleVendasPorDia = () => {
        navigateTo('/admin/financeiro/vendas-por-dia')
    }

    const handleMovimentoCaixa = () => {
        navigateTo('/admin/financeiro/movimento-caixa')
    }

    const handleRelatorioDiarioEvento = () => {
        navigateTo('/admin/financeiro/relatorio-diario-evento')
    }

    const handleRelatorioMovimentoCaixa = () => {
        navigateTo('/admin/relatorios/movimento-caixa')
    }

    const handleIngressosEmitidos = () => {
        navigateTo('/admin/relatorios/ingressos-emitidos')
    }

    const handleLogout = () => {
        localStorage.clear();
        navigate("/admin/login");
    };

    const carregarCaixaAberto = React.useCallback(async () => {
        try {
            const response = await getCaixaAbertoPedidosByUsuario();
            setTemCaixaAberto(Boolean(response.data));
        } catch {
            setTemCaixaAberto(false);
        }
    }, []);

    const carregarImpressoras = React.useCallback(async () => {
        setCarregandoImpressoras(true);

        try {
            const response = await api.get(`/auth/impressoras`);
            const data = response.data.data;

            setImpressoras(data);

            const salvaConfig = localStorage.getItem("impressora_config");

            if (salvaConfig) {
                const { ip, porta } = JSON.parse(salvaConfig);
                const existente = data.find((d: any) => d.ip === ip && d.porta === porta);

                if (existente) {
                    setImpressoraSelecionada(existente.id.toString());
                    return;
                }
            }

            // Se não havia nenhuma salva ou a salva não está mais disponível, seleciona a primeira
            if (data.length > 0) {
                const primeira = data[0];
                setImpressoraSelecionada(primeira.id.toString());

                localStorage.setItem(
                    "impressora_config",
                    JSON.stringify({
                        id: primeira.id,
                        nome: primeira.nome,
                        ip: primeira.ip,
                        porta: primeira.porta,
                        protocolo: primeira.protocolo,
                    })
                );
            }

        } catch (err: any) {
            if (err.response?.status === 401) {
                navigate("/login");
            }
        } finally {
            setCarregandoImpressoras(false);
        }
    }, [navigate]);


    const handleSelecionarImpressora = (id: string) => {
        const selecionada = impressoras.find((imp) => imp.id.toString() === id);
        if (selecionada) {
            setImpressoraSelecionada(id);
            savePrinterConfig(selecionada);
        }
    };



    React.useEffect(() => {
        carregarImpressoras();
    }, [carregarImpressoras]);

    React.useEffect(() => {
        carregarCaixaAberto();
    }, [carregarCaixaAberto]);

    return (
        <Box sx={{ flexGrow: 1 }}>
            <AppBar position="static">
                <Toolbar>
                    <IconButton size="large" edge="start" color="inherit" onClick={handleMenuOpen}>
                        <MenuIcon />
                    </IconButton>

                    <Link to="/admin">
                        <Box component="img" src={logo} alt="Logo Entrada Fácil" sx={{ maxHeight: 40, mx: 2, cursor: 'pointer' }} />
                    </Link>

                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        Área Administrativa
                    </Typography>

                    {temCaixaAberto ? (
                        <Button color="inherit" onClick={handleMovimentoCaixa} sx={{ mr: 2 }}>
                            Movimento de Caixa
                        </Button>
                    ) : null}

                    {/* Impressoras */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <FormControl size="small" sx={{ minWidth: 180 }}>
                            <InputLabel id="select-impressora-label" sx={{ color: "#fff" }}>
                                Impressora
                            </InputLabel>
                            <Select
                                labelId="select-impressora-label"
                                value={impressoraSelecionada}
                                onChange={(e) => handleSelecionarImpressora(e.target.value)}
                                disabled={carregandoImpressoras}
                                sx={{
                                    color: "white",
                                    ".MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                                    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                                    ".MuiSvgIcon-root": { color: "white" }
                                }}
                            >
                                {impressoras.length && impressoras.map((imp) => (
                                    <MenuItem key={imp.id} value={imp.id.toString()}>
                                        {imp.nome}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Tooltip title="Atualizar impressoras">
                            <IconButton onClick={carregarImpressoras} disabled={carregandoImpressoras} sx={{ color: "white" }}>
                                {carregandoImpressoras ? <CircularProgress size={20} color="inherit" /> : <Refresh />}
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Button color="inherit" startIcon={<LogoutIcon />} onClick={handleLogout}>
                        Sair
                    </Button>
                </Toolbar>
            </AppBar>

            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>

                <ListSubheader disableSticky>Geral</ListSubheader>
                <MenuItem onClick={handleInicio}>Início</MenuItem>
                <MenuItem onClick={handleCheckin}>Check-in</MenuItem>
                <MenuItem onClick={handleTestesImpressora}>Testes de Impressora</MenuItem>
                <Divider />
                <ListSubheader disableSticky>Cadastros</ListSubheader>
                <MenuItem onClick={handleEmpresas}>Empresas</MenuItem>
                <MenuItem onClick={handleTiposIngressos}>Tipos de Ingressos</MenuItem>
                <MenuItem onClick={handleIngressosCrud}>Ingressos</MenuItem>
                <Divider />
                <ListSubheader disableSticky>Financeiro</ListSubheader>
                <MenuItem onClick={handleMovimentoCaixa}>Movimento de Caixa</MenuItem>
                <MenuItem onClick={handlePedidos}>Pedidos</MenuItem>
                <Divider />
                <ListSubheader disableSticky>Relatórios</ListSubheader>
                <MenuItem onClick={handleIngressos}>Ingressos por empresa</MenuItem>
                <MenuItem onClick={handleVendasPorDia}>Vendas por dia</MenuItem>
                <MenuItem onClick={handleRelatorioDiarioEvento}>Relatório diário do evento</MenuItem>
                <MenuItem onClick={handleRelatorioMovimentoCaixa}>Relatório de Movimento de Caixa</MenuItem>
                <MenuItem onClick={handleIngressosEmitidos}>Ingressos emitidos</MenuItem>
            </Menu>

            <Container sx={{ mt: 4 }}>
                {children}
            </Container>
        </Box>
    );
}
